import type { Maybe } from '@adornis/base/utilTypes.js';
import { alertDialog, guardDialog, promptDialog } from '@adornis/dialog/dialog-functions.js';
import { UserAPI } from '@saraa/permissions/api/UserAPI.js';
import { SaraaUser } from '@saraa/permissions/db/SaraaUser.js';
import { html, nothing } from 'lit';

/** What the engine's `passwordValidator` enforces, said in the UI rather than only in the error. */
const PASSWORD_RULES = 'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

/**
 * Set a user's password.
 *
 * Ours rather than the ERM's `authenticationSection`, which called the engine's
 * `changePassword` — and that asserts the legacy `Users.ChangePassword`
 * permission against `user.roles`. Roles were removed when permissions moved to
 * memberships and `isPlatformAdmin`, so every reset failed with "Permission
 * 'Users.ChangePassword' not granted", whoever was asking. It goes through
 * `UserAPI.setPassword` instead, which is guarded by `Users.changePassword`
 * against the target user.
 *
 * The ERM section's other half — impersonate / stop impersonating — is
 * deliberately not carried over. It calls the engine's `impersonate`, which
 * asserts the legacy `Users.Impersonate` the same way and therefore fails for
 * everybody too; a button that always errors is worse than no button. (Stopping
 * an impersonation already in progress needs no permission and is still offered,
 * by `SessionActions`.)
 *
 * There is no control here to *read* whether a password is set. It is worth
 * knowing — a user created through `openCreateUserDialog` has none until this
 * runs, and cannot sign in until it does — but the engine's `isUserPasswordSet`
 * query goes through no guard of ours, so it stays out until it has one.
 */
const setPassword = async (user: { _id: string; email?: Maybe<string>; username?: Maybe<string> }) => {
  const label = user.email ?? user.username ?? 'this user';

  const password = await promptDialog(
    html`
      <div class="flex flex-col gap-1">
        <p class="text-base-content text-sm">Set a new password for ${label}.</p>
        <p class="text-base-content/60 text-xs">${PASSWORD_RULES}</p>
      </div>
    `,
    { placeholder: 'New password', buttonText: 'Set password', hidden: true },
    // Modal: a stray click outside a half-typed password should not discard it.
    { modal: true },
  ).catch(() => null);

  if (!password) return;

  /**
   * Checked here as well as on the server, and the server is the one that
   * decides. This only saves the round trip and puts the rule next to the field
   * it applies to — `resolveSetUserPassword` runs the same validator.
   */
  try {
    SaraaUser.passwordValidator({ value: password, key: 'password', target: {} });
  } catch (error) {
    await alertDialog(error instanceof Error ? error.message : PASSWORD_RULES);
    return;
  }

  await guardDialog(() => UserAPI.setPassword(user._id, password)(), {
    loadingText: 'Setting the password…',
    successText: `Password set for ${label}.`,
    errorMessagePrefix: 'Could not set the password',
    showLoaderModal: true,
  });
};

const PasswordControls = (user: { _id: string; email?: Maybe<string>; username?: Maybe<string> }) => html`
  <section class="flex flex-col gap-2">
    <p class="text-base-content text-sm font-semibold">Password</p>
    <p class="text-base-content/60 text-xs">
      Sets a new password immediately, without confirming the old one. A newly created account has none until this is
      done and cannot sign in before then. ${PASSWORD_RULES}
    </p>
    <button type="button" class="btn btn-primary btn-sm gap-2 self-start" @click=${() => void setPassword(user)}>
      <span class="icon-[material-symbols--key-vertical] text-lg"></span>
      Set password
    </button>
  </section>
`;

/** ERM detail section wrapper. */
export const UserPasswordSection = {
  // biome-ignore lint/suspicious/noExplicitAny: the ERM section context is entity-specific
  render: ({ entity }: { entity?: any }) => (entity?._id ? PasswordControls(entity) : nothing),
};
